import React, { ComponentProps, FC } from 'react';

import A from '@adsk/alloy-react-a';
import { Box, Flex } from '@adsk/alloy-react-box';
import { OpenIcon } from '@adsk/alloy-react-icon';
import { SimpleModal } from '@adsk/alloy-react-modal';
import Theme from '@adsk/alloy-react-theme';

import ACCImage from './assets/ACC_in-product.jpg';
import ACCLifecycle from './assets/Construction-Lifecycle.png';
import {
  HELP_LINK,
  LEARNING_CENTER_URL_1,
  LEARNING_CENTER_URL_2,
} from './helpers/const';
import {
  bodyStyle,
  footerHelpLinkStyle,
  imageStyle,
  imageTextLinkStyle,
} from './helpers/styles';
import { Subscription, SubscriptionConstruction } from '../../helpers/types';
import { useTranslation } from '../../i18n';
import TrialModalCarousel from '../TrialModalCarousel';
import TrialModalItem from '../TrialModalItem';

interface TrialModalProps {
  onClose: () => void;
  show: boolean;
  userTrials: Subscription[] | SubscriptionConstruction[];
}

const LearningCenterBox = (props: ComponentProps<typeof Box>) => (
  <Flex
    alignItems="center"
    flexDirection="row"
    justifyContent="space-between"
    paddingLeft="16px"
    {...props}
  />
);

const TrialModal: FC<TrialModalProps> = ({ onClose, show, userTrials }) => {
  const { t } = useTranslation();

  const getBannerContent = () => [
    <LearningCenterBox key="content-1">
      <div>
        <Box typography="bodySmallBold">
          {t('MODAL_IMAGE_TEXT_INTRODUCTION_1')}
        </Box>
        <Box typography="bodySmall">{t('MODAL_IMAGE_TEXT_DESCRIPTION_1')}</Box>
        <A
          style={imageTextLinkStyle}
          href={LEARNING_CENTER_URL_1}
          target="_blank"
        >
          {t('MODAL_IMAGE_TEXT_LINK')}
        </A>
      </div>
      <img style={imageStyle} src={ACCLifecycle} />
    </LearningCenterBox>,
    <LearningCenterBox key="content-0">
      <Box>
        <Box typography="bodySmallBold">
          {t('MODAL_IMAGE_TEXT_INTRODUCTION_2')}
        </Box>
        <Box typography="bodySmall">{t('MODAL_IMAGE_TEXT_DESCRIPTION_2')}</Box>
        <A
          style={imageTextLinkStyle}
          href={LEARNING_CENTER_URL_2}
          target="_blank"
        >
          {t('MODAL_IMAGE_TEXT_LINK')}
        </A>
      </Box>
      <img style={imageStyle} src={ACCImage} />
    </LearningCenterBox>,
  ];

  return (
    <SimpleModal
      title={t('MODAL_TITLE')?.toString()}
      onClose={() => {
        onClose();
      }}
      show={show}
      modalBodyProps={{ style: bodyStyle }}
    >
      <TrialModalCarousel>{getBannerContent()}</TrialModalCarousel>
      <Box padding="21px 0 8px 0">
        <Box typography="bodyMediumBold" paddingBottom="12px">
          {t('MODAL_CONTENT_TITLE')}
        </Box>
        {userTrials.map((userTrial) => (
          <TrialModalItem
            key={
              'offeringCategory' in userTrial
                ? userTrial.offeringCategory
                : userTrial.offeringName // Use a fallback key for SubscriptionConstruction
            }
            userTrial={userTrial}
          />
        ))}
      </Box>
      <Box
        borderTop={`1px solid ${Theme.colors.charcoal200}`}
        paddingTop="24px"
      >
        <A style={footerHelpLinkStyle} href={HELP_LINK} target="_blank">
          <Box paddingRight="15px" typography="bodyMediumBold">
            {t('MODAL_FOOTER_HELP_LINK')}
          </Box>
          <OpenIcon />
        </A>
      </Box>
    </SimpleModal>
  );
};

TrialModal.displayName = 'TrialModal';

export default TrialModal;
